/**
 * Agenda Service v2 — Consumes /api/vukz/agenda/events (P14 aggregator)
 *
 * Unified events from:
 * - Sports (competitions, matches)
 * - Training (plans, sessions)
 * - Agenda (existing events)
 */
import { AZURE } from './api';

/**
 * Fetch aggregated events (P14 Hub)
 * @param {{
 *   sport: string,
 *   clubId: number,
 *   from: string,
 *   to: string,
 *   category?: string,
 *   entityType?: 'athlete' | 'coach',
 *   entityId?: number,
 *   types?: string,
 *   championshipId?: number,
 *   planId?: number
 * }} opts
 * @returns {Promise<{events, total, filters}>}
 */
export const getAgendaEvents = async (opts = {}) => {
  const params = {
    sport: opts.sport,
    club_id: opts.clubId,
    from: opts.from,
    to: opts.to
  };
  
  if (opts.category) params.category = opts.category;
  if (opts.entityType) params.entity_type = opts.entityType;
  if (opts.entityId) params.entity_id = opts.entityId;
  if (opts.types) params.types = opts.types;
  if (opts.championshipId) params.championship_id = opts.championshipId;
  if (opts.planId) params.plan_id = opts.planId;
  
  const { data } = await AZURE.get('/vukz/agenda/events', { params });
  return data.data;
};

/**
 * Health check
 */
export const checkAgendaHealth = async () => {
  const { data } = await AZURE.get('/vukz/agenda/health');
  return data;
};

/**
 * Format event for display
 * @param {object} event - Raw event from API
 * @returns {object} - Formatted event with computed fields
 */
export const formatEvent = (event) => {
  const startDate = new Date(event.start_at);
  const endDate = new Date(event.end_at);
  
  return {
    ...event,
    // Display fields
    displayDate: startDate.toLocaleDateString('pt-BR'),
    displayTime: startDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    displayEndTime: endDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    // Computed
    isToday: startDate.toDateString() === new Date().toDateString(),
    isPast: startDate < new Date(),
    isFuture: startDate > new Date(),
    daysUntil: Math.ceil((startDate - new Date()) / (1000 * 60 * 60 * 24)),
    // Type helpers
    isCompetition: event.event_type === 'competition',
    isMatch: event.event_type === 'match',
    isTraining: ['training_plan', 'training_session'].includes(event.event_type),
    // Icons
    icon: getEventIcon(event.event_type),
    color: getEventColor(event.event_type)
  };
};

/**
 * Get icon for event type
 */
function getEventIcon(eventType) {
  const icons = {
    competition: '🏆',
    match: '⚽',
    training_plan: '📋',
    training_session: '🏃',
    default: '📅'
  };
  return icons[eventType] || icons.default;
}

/**
 * Get color for event type
 */
function getEventColor(eventType) {
  const colors = {
    competition: '#ef4444',    // Red
    match: '#3b82f6',          // Blue
    training_plan: '#10b981',  // Green
    training_session: '#8b5cf6', // Purple
    default: '#6b7280'         // Gray
  };
  return colors[eventType] || colors.default;
}

/**
 * Filter events by type
 * @param {Array} events - All events
 * @param {Array<string>} types - Types to include
 */
export const filterEventsByType = (events, types) => {
  if (!types || types.length === 0) return events;
  return events.filter(e => types.includes(e.event_type));
};

/**
 * Group events by date
 * @param {Array} events - Events array
 * @returns {Object} - { '2026-03-10': [...events], ... }
 */
export const groupEventsByDate = (events) => {
  return events.reduce((groups, event) => {
    const date = new Date(event.start_at).toISOString().split('T')[0];
    if (!groups[date]) groups[date] = [];
    groups[date].push(event);
    return groups;
  }, {});
};

/**
 * Get date range for current month
 * @returns {{ from: string, to: string }}
 */
export const getCurrentMonthRange = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  return {
    from: from.toISOString().split('T')[0],
    to: to.toISOString().split('T')[0]
  };
};

/**
 * Get date range for current week
 * @returns {{ from: string, to: string }}
 */
export const getCurrentWeekRange = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const from = new Date(now);
  from.setDate(now.getDate() - dayOfWeek);  // Start on Sunday
  const to = new Date(from);
  to.setDate(from.getDate() + 6);  // End on Saturday
  
  return {
    from: from.toISOString().split('T')[0],
    to: to.toISOString().split('T')[0]
  };
};
